import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/** ─── DEMO FLAG: set to false to use live API data ─── */
const USE_MOCK_DATA = true;

/**
 * Mock booking for demo — same patient (LNMP 2025-09-15, GA ~29+4 wks)
 */
const MOCK_BOOKING_BP = {
    id: 1001,
    patient_number: "DEMO-001",
    lnmp: "2025-09-15",
    edd: "2026-06-22",
    gravida: 2,
    parity: 1,
};

/**
 * Mock birth preparedness plan — completed at contact 3 (26 wks)
 */
const MOCK_PREPAREDNESS = {
    id: 501,
    booking_id: 1001,
    identified_birth_companion: true,
    companion_name: "John Nakato (Husband)",
    identified_facility: true,
    facility_name: "Mulago National Referral Hospital",
    transport_plan: true,
    transport_details: "Private car (husband's) — 20 min drive",
    financial_preparedness: true,
    savings_amount: "350000",
    identified_blood_donor: true,
    donor_name: "Grace Akello (Sister)",
    donor_blood_group: "O+",
    items_prepared: {
        clean_cloths: true,
        sterile_blade: true,
        cord_ties: true,
        warm_blanket: true,
        sanitary_pads: true,
        baby_clothes: true,
        mothers_clothes: true,
        basin: true,
        gloves: false,
        identity_documents: true,
        phone_credit: true,
        food_supplies: false,
    },
    danger_signs_counselled: [
        "vaginal_bleeding",
        "severe_headache",
        "blurred_vision",
        "convulsions",
        "swelling_face_hands",
        "reduced_fetal_movement",
        "fever",
        "abdominal_pain",
    ],
};

/**
 * Mock danger signs — one resolved episode of mild oedema at 20 wks
 */
const MOCK_DANGER_SIGNS = [
    {
        id: 601,
        booking_id: 1001,
        danger_sign: "swelling_face_hands",
        reported_date: "2026-02-02",
        action_taken: "Referred for BP check; urine protein dipstick done — trace protein only. Advised rest and low-salt diet.",
        outcome: "resolved",
    },
];

/* ─── Birth Preparedness Checklist Items ─── */
const PREPAREDNESS_ITEMS = [
    {
        key: "identified_birth_companion",
        label: "Birth Companion Identified",
        icon: "👤",
        detailField: "companion_name",
        detailLabel: "Companion Name",
        detailType: "text",
    },
    {
        key: "identified_facility",
        label: "Delivery Facility Identified",
        icon: "🏥",
        detailField: "facility_name",
        detailLabel: "Facility Name",
        detailType: "text",
    },
    {
        key: "transport_plan",
        label: "Transport Plan Arranged",
        icon: "🚗",
        detailField: "transport_details",
        detailLabel: "Transport Details",
        detailType: "text",
    },
    {
        key: "financial_preparedness",
        label: "Financial Savings Ready",
        icon: "💰",
        detailField: "savings_amount",
        detailLabel: "Amount Saved (UGX)",
        detailType: "number",
    },
    {
        key: "identified_blood_donor",
        label: "Blood Donor Identified",
        icon: "🩸",
        detailField: "donor_name",
        detailLabel: "Donor Name",
        detailType: "text",
        extraField: "donor_blood_group",
        extraLabel: "Donor Blood Group",
        extraOptions: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"],
    },
];

/* ─── Items to Prepare Checklist ─── */
const ITEMS_TO_PREPARE = [
    { key: "clean_cloths", label: "Clean cloths / towels" },
    { key: "sterile_blade", label: "New sterile razor blade" },
    { key: "cord_ties", label: "Clean cord ties / string" },
    { key: "warm_blanket", label: "Warm blanket for baby" },
    { key: "sanitary_pads", label: "Sanitary pads" },
    { key: "baby_clothes", label: "Baby clothes" },
    { key: "mothers_clothes", label: "Mother's change of clothes" },
    { key: "basin", label: "Clean basin / container" },
    { key: "gloves", label: "Gloves (pair)" },
    { key: "identity_documents", label: "Identity documents / ANC card" },
    { key: "phone_credit", label: "Phone credit for emergency calls" },
    { key: "food_supplies", label: "Food and drinks for labour" },
];

/* ─── Danger Signs Definitions ─── */
const DANGER_SIGNS = [
    { key: "vaginal_bleeding", label: "Vaginal Bleeding", icon: "🩸", severity: "critical", description: "Any vaginal bleeding during pregnancy" },
    { key: "severe_headache", label: "Severe Headache", icon: "🤕", severity: "high", description: "Persistent, severe headache not relieved by paracetamol" },
    { key: "blurred_vision", label: "Blurred Vision", icon: "👁", severity: "high", description: "Visual disturbances, blurring, or seeing spots" },
    { key: "convulsions", label: "Convulsions / Fits", icon: "⚡", severity: "critical", description: "Seizures or loss of consciousness" },
    { key: "swelling_face_hands", label: "Swelling of Face/Hands", icon: "🖐", severity: "high", description: "Sudden swelling of face, hands, or feet" },
    { key: "reduced_fetal_movement", label: "Reduced Fetal Movement", icon: "👶", severity: "high", description: "Noticeably less baby movement than usual" },
    { key: "fever", label: "High Fever", icon: "🌡", severity: "medium", description: "Temperature above 38°C with or without chills" },
    { key: "abdominal_pain", label: "Severe Abdominal Pain", icon: "😣", severity: "high", description: "Severe lower abdominal pain, not related to labour" },
    { key: "water_breaking", label: "Water Breaking Early", icon: "💧", severity: "high", description: "Leaking fluid from vagina before labour" },
];

/**
 * useBirthPreparedness — Custom hook for Birth Preparedness & Danger Signs
 */
export const useBirthPreparedness = function(actions, patient_number, visit_id) {

    /* ─── Booking State ─── */
    const _bookingData = useState(null);
    const bookingData = _bookingData[0];
    const setBookingData = _bookingData[1];

    const _loadingBooking = useState(true);
    const loadingBooking = _loadingBooking[0];
    const setLoadingBooking = _loadingBooking[1];

    /* ─── Preparedness State ─── */
    const _preparedness = useState(null);
    const preparedness = _preparedness[0];
    const setPreparedness = _preparedness[1];

    const _loadingPrep = useState(false);
    const loadingPrep = _loadingPrep[0];
    const setLoadingPrep = _loadingPrep[1];

    const _prepForm = useState({
        identified_birth_companion: false,
        companion_name: "",
        identified_facility: false,
        facility_name: "",
        transport_plan: false,
        transport_details: "",
        financial_preparedness: false,
        savings_amount: "",
        identified_blood_donor: false,
        donor_name: "",
        donor_blood_group: "",
        items_prepared: {},
        danger_signs_counselled: [],
    });
    const prepForm = _prepForm[0];
    const setPrepForm = _prepForm[1];

    const _savingPrep = useState(false);
    const savingPrep = _savingPrep[0];
    const setSavingPrep = _savingPrep[1];

    /* ─── Danger Signs State ─── */
    const _dangerSigns = useState([]);
    const dangerSigns = _dangerSigns[0];
    const setDangerSigns = _dangerSigns[1];

    const _loadingDanger = useState(false);
    const loadingDanger = _loadingDanger[0];
    const setLoadingDanger = _loadingDanger[1];

    const _dangerForm = useState({
        danger_sign: "",
        reported_date: new Date().toISOString().slice(0, 10),
        action_taken: "",
        outcome: "",
    });
    const dangerForm = _dangerForm[0];
    const setDangerForm = _dangerForm[1];

    const _dangerDialog = useState(false);
    const dangerDialog = _dangerDialog[0];
    const setDangerDialog = _dangerDialog[1];

    const _savingDanger = useState(false);
    const savingDanger = _savingDanger[0];
    const setSavingDanger = _savingDanger[1];

    /* ─── Active Tab ─── */
    const _activeTab = useState("preparedness");
    const activeTab = _activeTab[0];
    const setActiveTab = _activeTab[1];

    /* ═══ Fetch booking (or load mock) ═══ */
    useEffect(function() {
        if (USE_MOCK_DATA) {
            setBookingData(MOCK_BOOKING_BP);
            setLoadingBooking(false);
            return;
        }
        if (!patient_number) return;
        setLoadingBooking(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/booking/" + patient_number)
            .then(function(res) {
                const data = res.data || {};
                if (data.id) setBookingData(data);
            })
            .catch(function() { setBookingData(null); })
            .finally(function() { setLoadingBooking(false); });
    }, [patient_number]);

    /* ═══ Fetch preparedness (or load mock) ═══ */
    useEffect(function() {
        if (USE_MOCK_DATA) {
            const data = MOCK_PREPAREDNESS;
            setPreparedness(data);
            const items = data.items_prepared || {};
            const counselled = data.danger_signs_counselled || [];
            setPrepForm({
                identified_birth_companion: !!data.identified_birth_companion,
                companion_name: data.companion_name || "",
                identified_facility: !!data.identified_facility,
                facility_name: data.facility_name || "",
                transport_plan: !!data.transport_plan,
                transport_details: data.transport_details || "",
                financial_preparedness: !!data.financial_preparedness,
                savings_amount: data.savings_amount || "",
                identified_blood_donor: !!data.identified_blood_donor,
                donor_name: data.donor_name || "",
                donor_blood_group: data.donor_blood_group || "",
                items_prepared: items,
                danger_signs_counselled: counselled,
            });
            setLoadingPrep(false);
            return;
        }
        if (!bookingData || !bookingData.id) return;
        setLoadingPrep(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/birth-preparedness/" + bookingData.id)
            .then(function(res) {
                const data = res.data || {};
                if (data.id) {
                    setPreparedness(data);
                    // Populate form from existing data
                    let items = {};
                    try { items = typeof data.items_prepared === "string" ? JSON.parse(data.items_prepared) : (data.items_prepared || {}); } catch(e) { items = {}; }
                    let counselled = [];
                    try { counselled = typeof data.danger_signs_counselled === "string" ? JSON.parse(data.danger_signs_counselled) : (data.danger_signs_counselled || []); } catch(e) { counselled = []; }
                    setPrepForm({
                        identified_birth_companion: !!data.identified_birth_companion,
                        companion_name: data.companion_name || "",
                        identified_facility: !!data.identified_facility,
                        facility_name: data.facility_name || "",
                        transport_plan: !!data.transport_plan,
                        transport_details: data.transport_details || "",
                        financial_preparedness: !!data.financial_preparedness,
                        savings_amount: data.savings_amount || "",
                        identified_blood_donor: !!data.identified_blood_donor,
                        donor_name: data.donor_name || "",
                        donor_blood_group: data.donor_blood_group || "",
                        items_prepared: items,
                        danger_signs_counselled: counselled,
                    });
                }
            })
            .catch(function() {})
            .finally(function() { setLoadingPrep(false); });
    }, [bookingData]);

    /* ═══ Fetch danger signs (or load mock) ═══ */
    useEffect(function() {
        if (USE_MOCK_DATA) {
            setDangerSigns(MOCK_DANGER_SIGNS);
            setLoadingDanger(false);
            return;
        }
        if (!bookingData || !bookingData.id) return;
        setLoadingDanger(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/danger-signs/" + bookingData.id)
            .then(function(res) {
                const data = res.data || [];
                if (Array.isArray(data)) setDangerSigns(data);
            })
            .catch(function() { setDangerSigns([]); })
            .finally(function() { setLoadingDanger(false); });
    }, [bookingData]);

    /* ═══ Preparedness Form Handlers ═══ */
    const handlePrepChange = useCallback(function(field, value) {
        setPrepForm(function(prev) {
            const obj = {};
            obj[field] = value;
            return Object.assign({}, prev, obj);
        });
    }, []);

    const togglePrepItem = useCallback(function(key) {
        setPrepForm(function(prev) {
            const obj = {};
            obj[key] = !prev[key];
            return Object.assign({}, prev, obj);
        });
    }, []);

    const toggleItemPrepared = useCallback(function(itemKey) {
        setPrepForm(function(prev) {
            const items = Object.assign({}, prev.items_prepared);
            items[itemKey] = !items[itemKey];
            return Object.assign({}, prev, { items_prepared: items });
        });
    }, []);

    const toggleCounselledSign = useCallback(function(signKey) {
        setPrepForm(function(prev) {
            const arr = prev.danger_signs_counselled.slice();
            const idx = arr.indexOf(signKey);
            if (idx >= 0) {
                arr.splice(idx, 1);
            } else {
                arr.push(signKey);
            }
            return Object.assign({}, prev, { danger_signs_counselled: arr });
        });
    }, []);

    /* ═══ Save Preparedness ═══ */
    const savePreparedness = useCallback(function() {
        if (!bookingData) return;
        setSavingPrep(true);

        const payload = Object.assign({}, prepForm, {
            booking_id: bookingData.id,
            visit_id: visit_id,
            items_prepared: JSON.stringify(prepForm.items_prepared),
            danger_signs_counselled: JSON.stringify(prepForm.danger_signs_counselled),
        });

        const request = preparedness && preparedness.id
            ? axios.put(config.smsUrl + "/obgyn/anc/birth-preparedness/" + preparedness.id, payload)
            : axios.post(config.smsUrl + "/obgyn/anc/birth-preparedness", payload);

        request
            .then(function(res) {
                const data = res.data || payload;
                setPreparedness(data);
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackbarOpen("success", "Birth preparedness plan saved successfully");
                }
            })
            .catch(function() {
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackbarOpen("error", "Failed to save birth preparedness plan");
                }
            })
            .finally(function() { setSavingPrep(false); });
    }, [bookingData, visit_id, prepForm, preparedness, actions]);

    /* ═══ Danger Sign Form Handlers ═══ */
    const handleDangerFormChange = useCallback(function(field, value) {
        setDangerForm(function(prev) {
            const obj = {};
            obj[field] = value;
            return Object.assign({}, prev, obj);
        });
    }, []);

    const openDangerDialog = useCallback(function(signKey) {
        setDangerForm({
            danger_sign: signKey || "",
            reported_date: new Date().toISOString().slice(0, 10),
            action_taken: "",
            outcome: "",
        });
        setDangerDialog(true);
    }, []);

    const closeDangerDialog = useCallback(function() {
        setDangerDialog(false);
    }, []);

    const submitDangerSign = useCallback(function() {
        if (!bookingData || !dangerForm.danger_sign) return;
        setSavingDanger(true);

        const payload = Object.assign({}, dangerForm, {
            booking_id: bookingData.id,
            visit_id: visit_id,
        });

        axios
            .post(config.smsUrl + "/obgyn/anc/danger-sign", payload)
            .then(function(res) {
                const data = res.data || payload;
                setDangerSigns(function(prev) { return prev.concat([data]); });
                if (actions && actions.snackbarActions) {
                    const label = DANGER_SIGNS.find(function(s) { return s.key === dangerForm.danger_sign; });
                    actions.snackbarActions.snackbarOpen("warning",
                        "⚠ Danger sign reported: " + (label ? label.label : dangerForm.danger_sign));
                }
                closeDangerDialog();
            })
            .catch(function() {
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackbarOpen("error", "Failed to report danger sign");
                }
            })
            .finally(function() { setSavingDanger(false); });
    }, [bookingData, visit_id, dangerForm, actions, closeDangerDialog]);

    /* ═══ Computed: Completion Percentage ═══ */
    const completionStats = useMemo(function() {
        const totalChecklist = PREPAREDNESS_ITEMS.length;
        let checkedCount = 0;
        PREPAREDNESS_ITEMS.forEach(function(item) {
            if (prepForm[item.key]) checkedCount++;
        });

        const totalItems = ITEMS_TO_PREPARE.length;
        let preparedCount = 0;
        ITEMS_TO_PREPARE.forEach(function(item) {
            if (prepForm.items_prepared && prepForm.items_prepared[item.key]) preparedCount++;
        });

        const totalCounselling = DANGER_SIGNS.length;
        const counselledCount = prepForm.danger_signs_counselled ? prepForm.danger_signs_counselled.length : 0;

        const overallTotal = totalChecklist + totalItems + totalCounselling;
        const overallDone = checkedCount + preparedCount + counselledCount;
        const overallPercent = overallTotal > 0 ? Math.round((overallDone / overallTotal) * 100) : 0;

        return {
            checklistDone: checkedCount,
            checklistTotal: totalChecklist,
            checklistPercent: totalChecklist > 0 ? Math.round((checkedCount / totalChecklist) * 100) : 0,
            itemsDone: preparedCount,
            itemsTotal: totalItems,
            itemsPercent: totalItems > 0 ? Math.round((preparedCount / totalItems) * 100) : 0,
            counsellingDone: counselledCount,
            counsellingTotal: totalCounselling,
            counsellingPercent: totalCounselling > 0 ? Math.round((counselledCount / totalCounselling) * 100) : 0,
            overallDone: overallDone,
            overallTotal: overallTotal,
            overallPercent: overallPercent,
        };
    }, [prepForm]);

    /* ═══ Computed: Active Danger Signs (recent, unresolved) ═══ */
    const activeDangerSigns = useMemo(function() {
        return dangerSigns.filter(function(ds) {
            return !ds.outcome || ds.outcome === "" || ds.outcome === "pending";
        });
    }, [dangerSigns]);

    /* ═══ Computed: Gestational Age ═══ */
    const gestationalAge = useMemo(function() {
        if (!bookingData || !bookingData.lnmp) return null;
        const lnmpDate = new Date(bookingData.lnmp);
        const todayDate = new Date();
        const diffMs = todayDate - lnmpDate;
        if (diffMs < 0) return null;
        return Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
    }, [bookingData]);

    return {
        bookingData: bookingData,
        loadingBooking: loadingBooking,
        gestationalAge: gestationalAge,
        /* Preparedness */
        preparedness: preparedness,
        loadingPrep: loadingPrep,
        prepForm: prepForm,
        handlePrepChange: handlePrepChange,
        togglePrepItem: togglePrepItem,
        toggleItemPrepared: toggleItemPrepared,
        toggleCounselledSign: toggleCounselledSign,
        savePreparedness: savePreparedness,
        savingPrep: savingPrep,
        completionStats: completionStats,
        /* Danger Signs */
        dangerSigns: dangerSigns,
        activeDangerSigns: activeDangerSigns,
        loadingDanger: loadingDanger,
        dangerForm: dangerForm,
        handleDangerFormChange: handleDangerFormChange,
        openDangerDialog: openDangerDialog,
        closeDangerDialog: closeDangerDialog,
        submitDangerSign: submitDangerSign,
        savingDanger: savingDanger,
        dangerDialog: dangerDialog,
        /* Tabs */
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        /* Constants */
        PREPAREDNESS_ITEMS: PREPAREDNESS_ITEMS,
        ITEMS_TO_PREPARE: ITEMS_TO_PREPARE,
        DANGER_SIGNS: DANGER_SIGNS,
    };
};
